import React from 'react';
import Flexbox from 'flexbox-react';
import { css } from 'emotion';
import TransformationParameter from 'components/core/fields/transformation_parameters/TransformationParameter';
import MessageBox from 'components/core/MessageBox';
import OverrideTable from 'components/core/fields/transformation_parameters/OverrideTable';
import RIENumber from 'components/core/editable/RIENumber';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import { colorTextGrey } from 'styles/base.js';

export default class StandardDeviationParamter extends TransformationParameter {
    static defaultProps = {
        parameterNames: ['std_ind_max', 'std_bad_max'],
        isOverridable: true,
        isDefaultValueVisibleInitially: false,
        isOverrideVisibleInitially: true,
        infoBoxText: '',
    };

    renderMessageBox = () => {
        return (
            <MessageBox>
                <Flexbox>
                    <div>
                        The data transformation libraries calculate the standard deviation of all the input values that are averaged to compute an output data point.  This calculation is done for use as a QC threshold.
                    </div>
                </Flexbox>
            </MessageBox>
        );
    };

    renderQuestionText = () => {
        return 'Would you like to set a QC threshold for the standard deviation by dimension?';
    };

    renderOverrideQuestionText = () => {
        return 'Would you like to set a QC threshold for the standard deviation by variable?';
    };

    renderDefaultComponent = () => {
        const indValue = this.getCoordinateSystemDefaultValue('std_ind_max');
        const badValue = this.getCoordinateSystemDefaultValue('std_bad_max');

        return (
            <Flexbox flexDirection={'column'} className={css`width:100%;padding-left:8px;`}>
                <Flexbox flexDirection={'row'} alignItems={'center'}>
                    <InfoBoxLabel
                        alignTop
                        className="noshrink"
                        label="Indeterminate Limit"
                        message={''}
                    />
                    <RIENumber
                        value={indValue}
                        change={this.onDefaultValueChange}
                        fieldPath={[]}
                        onChangeData={{parameterName: 'std_ind_max'}}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                        isDisabled={!this.props.hasWritePermissions}
                    />
                </Flexbox>
                <Flexbox flexDirection={'row'} alignItems={'center'}>
                    <InfoBoxLabel
                        alignTop
                        className="noshrink"
                        label="Bad Value"
                        message={''}
                    />
                    <RIENumber
                        value={badValue}
                        change={this.onDefaultValueChange}
                        fieldPath={[]}
                        onChangeData={{parameterName: 'std_bad_max'}}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                        isDisabled={!this.props.hasWritePermissions}
                    />
                </Flexbox>
            </Flexbox>
        );
    };

    renderOverrideTable = () => {
        return (
            <StandardDeviationOverrideTable
                process={this.props.process}
                processErrors={this.props.processErrors}
                coordinateSystemName={this.props.coordinateSystemName}
                dimensionName={this.props.dimensionName}
                parameterNames={this.props.parameterNames}
                hasWritePermissions={this.props.hasWritePermissions}
            />
        );
    }
}

class StandardDeviationOverrideTable extends OverrideTable {

    renderIndQC = (data) => {
        const parameterName = 'std_ind_max';
        const onChangeData = Object.assign({}, data, { parameterName });
        const value = this.getValue(data, parameterName);

        return (
            <RIENumber
                value={value}
                change={this.onValueChange}
                fieldPath={this.getFieldPath(parameterName, data)}
                onChangeData={onChangeData}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                formDisplay
                isDisabled={!this.props.hasWritePermissions}
            />
        );
    };

    renderBadQC = (data) => {
        const parameterName = 'std_bad_max';
        const onChangeData = Object.assign({}, data, { parameterName });
        const value = this.getValue(data, parameterName);

        return (
            <RIENumber
                value={value}
                change={this.onValueChange}
                fieldPath={this.getFieldPath(parameterName, data)}
                onChangeData={onChangeData}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                formDisplay
                isDisabled={!this.props.hasWritePermissions}
            />
        );
    };

    renderHeader = (labelText, descriptionText) => {
        const labelStyle = css`
            font-weight: 700;
        `;
        const descStyle = css`
            font-size: 11px;
            color: ${colorTextGrey};
            font-weight: normal;
            text-align: center;
        `;
        return (
            <Flexbox flexDirection="column" alignItems="center">
                <div className={labelStyle}>{labelText}</div>
                <div className={descStyle}>{descriptionText}</div>
            </Flexbox>
        );
    };

    getAdditionalColumns() {
        return ([
            {
                property: 'data',
                header: {
                    label: 'Indeterminate Limit',
                    formatters: [
                        data => {
                            return this.renderHeader('Indeterminate Limit',
                                '(data points with standard deviation above this limit will be marked as indeterminate)');
                        }
                    ],
                    props: {
                        style: {
                            width: '25%',
                            minWidth: '246px'
                        }
                    }
                },
                cell: {
                    formatters: [
                        data => { return this.renderIndQC(data); }
                    ]
                }
            },
            {
                property: 'data',
                header: {
                    label: 'Bad Limit',
                    formatters: [
                        data => {
                            return this.renderHeader('Bad Limit',
                                '(data points with standard deviation above this limit will be marked as bad)');
                        }
                    ],
                    props: {
                        style: {
                            width: '25%',
                            minWidth: '246px'
                        }
                    }
                },
                cell: {
                    formatters: [
                        data => { return this.renderBadQC(data); }
                    ]
                }
            }
        ]);
    }
}




// WEBPACK FOOTER //
// ./src/components/core/fields/transformation_parameters/StandardDeviationParamter.js